import { useState } from 'react'
import { Button } from '@web/components/ui/Button'
import { useSendPush, type PushSendReport } from '@web/lib/admin'

/**
 * Sending a one-off notification to a student's phone.
 *
 * A push cannot be recalled, cannot be edited, and lands on a lock screen
 * somebody may be looking at seconds later. So the compose box does not have a
 * send button at all: writing and sending are two separate steps, with the
 * message rendered as it will actually appear in between. The extra tap is the
 * feature — it is the difference between sending a half-written sentence and
 * noticing it first.
 */

const TITLE_MAX = 50
const BODY_MAX = 150

/** Where a tap on the notification lands. Kept to real destinations. */
const DESTINATIONS = [
  { value: '/', label: 'Home' },
  { value: '/review', label: 'Review' },
  { value: '/course', label: 'Lessons' },
  { value: '/phrasebook', label: 'Phrasebook' },
] as const

export function SendPushCard({
  userId,
  studentName,
  devices,
}: {
  userId: string
  studentName: string
  devices: number
}) {
  // Deliberately empty rather than defaulting to "VIVA": iOS already shows the
  // app name above the notification, so a title of "VIVA" renders the word
  // twice, one above the other.
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [url, setUrl] = useState<string>('/')
  const [confirming, setConfirming] = useState(false)
  const send = useSendPush(userId)

  const trimmedBody = body.trim()
  const trimmedTitle = title.trim()
  const ready = trimmedTitle.length > 0 && trimmedBody.length > 0

  function reset() {
    setBody('')
    setConfirming(false)
    send.reset()
  }

  async function confirmSend() {
    await send.mutateAsync({ title: trimmedTitle, body: trimmedBody, url })
    setBody('')
    setConfirming(false)
  }

  return (
    <section className="rounded-card bg-white p-5 ring-1 ring-brand-900/5 md:col-span-2">
      <h2 className="font-display text-lg font-bold text-ink-900">Send a notification</h2>
      <p className="mt-1 text-sm text-ink-500">
        {devices === 0
          ? `${studentName} has no devices subscribed — nothing can be sent until she turns notifications on.`
          : `Goes to ${studentName}'s ${devices === 1 ? 'device' : `${devices} devices`}. It cannot be taken back.`}
      </p>

      {send.data && <SentReport report={send.data} onAgain={reset} />}

      {!send.data &&
        (confirming ? (
          <Confirm
            title={trimmedTitle}
            body={trimmedBody}
            destination={DESTINATIONS.find((d) => d.value === url)?.label ?? url}
            studentName={studentName}
            devices={devices}
            busy={send.isPending}
            error={send.error}
            onBack={() => setConfirming(false)}
            onSend={() => void confirmSend()}
          />
        ) : (
          <fieldset disabled={devices === 0} className="mt-4 space-y-3">
            <Field label="Title" hint={`${trimmedTitle.length}/${TITLE_MAX}`}>
              <input
                type="text"
                value={title}
                maxLength={TITLE_MAX}
                placeholder="Um recadinho"
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm"
              />
            </Field>

            <Field label="Message" hint={`${trimmedBody.length}/${BODY_MAX}`}>
              <textarea
                value={body}
                maxLength={BODY_MAX}
                rows={3}
                placeholder="Bom dia! Twenty words are waiting for you."
                onChange={(e) => setBody(e.target.value)}
                className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm"
              />
            </Field>

            <Field label="Opens">
              <select
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm"
              >
                {DESTINATIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </Field>

            <Button disabled={!ready} onClick={() => setConfirming(true)}>
              Preview
            </Button>
          </fieldset>
        ))}
    </section>
  )
}

/**
 * The message as a lock screen will render it, then the confirm.
 *
 * Shown at notification proportions rather than as a form summary: the point
 * is to read it the way she will read it, which is how a sentence that seemed
 * fine in a textarea reveals itself as abrupt.
 */
function Confirm({
  title,
  body,
  destination,
  studentName,
  devices,
  busy,
  error,
  onBack,
  onSend,
}: {
  title: string
  body: string
  destination: string
  studentName: string
  devices: number
  busy: boolean
  error: unknown
  onBack: () => void
  onSend: () => void
}) {
  return (
    <div className="mt-4 space-y-3">
      <div className="rounded-2xl bg-sand-100 p-4">
        <div className="rounded-xl bg-white/90 p-3 shadow-sm ring-1 ring-brand-900/5">
          <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">VIVA</p>
          <p className="mt-1 font-semibold text-ink-900">{title}</p>
          <p className="text-sm text-ink-700">{body}</p>
        </div>
        <p className="mt-2 text-center text-xs text-ink-500">Opens {destination}</p>
      </div>

      <p className="text-sm text-ink-700">
        Send to {studentName}
        {devices > 1 && ` on ${devices} devices`}?
      </p>

      {error !== null && error !== undefined && (
        <p role="alert" className="rounded-xl bg-clay-50 px-3 py-2 text-sm text-clay-700">
          {error instanceof Error ? error.message : 'It could not be sent.'}
        </p>
      )}

      <div className="flex gap-2">
        <Button disabled={busy} onClick={onSend}>
          {busy ? 'Sending…' : 'Send now'}
        </Button>
        <button
          type="button"
          disabled={busy}
          onClick={onBack}
          className="min-h-11 px-3 text-sm font-semibold text-ink-500 hover:text-ink-700"
        >
          Back to editing
        </button>
      </div>
    </div>
  )
}

/**
 * What actually happened, per device.
 *
 * `gone` is reported rather than hidden: it means a device the notification
 * could not reach and whose subscription has now been retired, which is the
 * one outcome worth knowing about — it is why "I sent it" and "she saw it" can
 * differ.
 */
function SentReport({ report, onAgain }: { report: PushSendReport; onAgain: () => void }) {
  const nothing = report.sent === 0

  return (
    <div className="mt-4 space-y-2">
      <p
        role="status"
        className={`rounded-xl px-3 py-2 text-sm ${
          nothing ? 'bg-gold-100 text-gold-700' : 'bg-brand-100 text-brand-800'
        }`}
      >
        {nothing
          ? 'Not delivered to any device.'
          : `Sent to ${report.sent} ${report.sent === 1 ? 'device' : 'devices'}.`}
        {report.gone > 0 && ` ${report.gone} unsubscribed and ${report.gone === 1 ? 'was' : 'were'} removed.`}
        {report.failed > 0 && ` ${report.failed} failed and can be retried.`}
      </p>
      <button
        type="button"
        onClick={onAgain}
        className="min-h-11 text-sm font-semibold text-brand-600 hover:text-brand-700"
      >
        Write another
      </button>
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-baseline justify-between">
        <span className="text-sm font-medium text-ink-900">{label}</span>
        {hint && <span className="text-xs tabular-nums text-ink-400">{hint}</span>}
      </span>
      {children}
    </label>
  )
}
